// #417 — runs once on app init. Resolves the visitor's AU state via the
// multi-signal cascade and seeds GlobalState.detectedState. Manual selection
// (selectedState in localStorage) takes priority over detection — we still
// run detection so the State Selector UI can show "We detected: NSW — change?"
// on top of any saved selection if confidence is high.
//
// Logged-in customer with an AU default address is treated as a starting
// guess (~70% confidence) — Tena orders for elderly relatives shipping to
// another state are common, so the State Selector must remain prominent.
//
// Consent handling (OneTrust):
//   - Timezone + Shopify.customerPrivacy.getRegion() + customer default_address
//     all run unconditionally. None are 3rd-party network calls and none
//     require opt-in: timezone is a browser primitive; getRegion IS the
//     consent infrastructure; default_address is the logged-in user's own
//     stored data.
//   - The IP geolocation call (ipinfo.io) is 3rd-party tracking-adjacent —
//     gated on C0003 (Functional) consent. If consent isn't granted at
//     bootstrap time, we skip it and re-run the cascade when
//     OneTrustGroupsUpdated fires with C0003. On stores where OneTrust
//     isn't installed (e.g. tena-staging), `hasConsent` fails open so the
//     IP signal still runs.
//   - The `tena__selected-state` localStorage key is treated as strictly
//     necessary — same justification as `tena__sample-cart` etc.: essential
//     for the user to see correct stock availability for their state.

import { GlobalState } from '../global-state/global-state';
import {
  detectAuState,
  emitDetectionTelemetry,
  normaliseAuState,
} from './geo-state';
import { hasConsent, onConsentChange } from './onetrust-consent';
import { STATE_EVENTS } from './state-events';

const FUNCTIONAL_CONSENT = 'C0003';

function seedFromCustomerDefaultAddress() {
  const defaultProvince = (
    window.theme &&
    window.theme.customer &&
    window.theme.customer.default_address &&
    window.theme.customer.default_address.province_code
  ) || null;
  if (!defaultProvince) return false;
  const normalised = normaliseAuState(defaultProvince);
  if (!normalised) return false;
  GlobalState.setDetectedState(normalised, 'MEDIUM');
  emitDetectedEvent(normalised, 'MEDIUM');
  return true;
}

function emitDetectedEvent(state, confidence) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(STATE_EVENTS.DETECTED, {
    detail: { state, confidence },
  }));
}

function runCascade({ skipIpGeo }) {
  return detectAuState({ skipIpGeo })
    .then((result) => {
      emitDetectionTelemetry(result);
      // Don't override an existing detectedState unless HIGH confidence.
      const existing = GlobalState.state.detectedState;
      const willUpdate = !existing || result.confidence === 'HIGH';
      if (willUpdate && result.state) {
        GlobalState.setDetectedState(result.state, result.confidence);
        emitDetectedEvent(result.state, result.confidence);
      }
      return result;
    })
    .catch(() => null);
}

export default function StateDetectionBootstrap() {
  // Step 1 — default address for logged-in users. Synchronous seed so UI
  // doesn't flash empty while the async cascade completes.
  seedFromCustomerDefaultAddress();

  // Step 2 — initial cascade. Skip ipinfo.io if we don't have functional
  // consent yet (OneTrust auto-block would likely block it anyway, but we
  // skip explicitly so telemetry is accurate).
  const canCallIpGeo = hasConsent(FUNCTIONAL_CONSENT);
  runCascade({ skipIpGeo: !canCallIpGeo });

  // Step 3 — if consent wasn't there at bootstrap, listen for the user to
  // grant it later (via the cookie banner) and re-run the cascade with
  // the IP signal enabled at that point.
  if (!canCallIpGeo) {
    const unsubscribe = onConsentChange((activeGroups) => {
      if (activeGroups.includes(FUNCTIONAL_CONSENT)) {
        unsubscribe();
        runCascade({ skipIpGeo: false });
      }
    });
  }
}
